import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Data } from '@app/shared/components/mat-chip/mat-chip.component';
import { Labels } from '@app/util/labels.util';
import { FormBuilder, FormGroup } from '@angular/forms';

export interface WorkflowData extends Data {
  name: string,
  target: WorkflowData | undefined,
}

@Component({
  selector: 'app-iw-add-wf',
  templateUrl: './iw-add-wf.component.html',
  styleUrls: ['./iw-add-wf.component.scss']
})
export class IwAddWfComponent implements OnInit {

  /****************** Variable declaration ******************/
  /**
   * Labels used in the components
   */
  wfName: String = Labels.wfName;
  target: String = Labels.target;

  workflows: any[]
  selectedWorkflow: any;
  selectedTarget: WorkflowData;
  inputValues: { [key: string]: string[] } = {};
  additionalFields: any[] = [];
  showAdditionalFields = false;
  workflowSelected: boolean = false;
  additionalFieldsForm: FormGroup;
  parametersForm: FormGroup;
  newField = {
    key: '',
    value: '',
    description: ''
  };

  targetWfs: WorkflowData[]

  /****************** Input and Output decorators ******************/
  @Output() dataSaved: EventEmitter<WorkflowData> = new EventEmitter<WorkflowData>();

  /**
   * Constructor
   * @param data 
   * @param dialogRef 
   * @param fb 
   */
  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<IwAddWfComponent>,
    private fb: FormBuilder) {
    this.workflows = data.workflows;
    this.targetWfs = data.targetWfs;
    this.selectedWorkflow = data.selectedWorkflow;
    this.inputValues = data.inputValues;

    this.additionalFieldsForm = this.fb.group({
      latest: [false],
      key: [''],
      description: [''],
      value: [{ value: '', disabled: false }],
    });

    this.parametersForm = this.fb.group({});

    // Subscribe to the changes of the 'latest' checkbox form control
    this.additionalFieldsForm.get('latest')?.valueChanges.subscribe((value) => {
      // When the checkbox is clicked, disable/enable the 'value' input field
      if (value) {
        this.additionalFieldsForm.get('value')?.disable();
      } else {
        this.additionalFieldsForm.get('value')?.enable();
      }
    });
  }

  /**ngOnInit */
  ngOnInit(): void { }

  /**
   * addNewField function is used to show additional field in input fields
   */
  addNewField(): void {
    this.showAdditionalFields = true;
  }

  /**
   * Check if the workflow dropdown value is selected or not
   */
  onWorkflowSelected() {
    this.workflowSelected = !!this.selectedWorkflow; // Update the variable based on workflow selection
  }

  /**
   * cancelNewField function is used to cancel the additional field added
   */
  cancelNewField(): void {
    this.showAdditionalFields = false;
    this.newField = {
      key: '',
      value: '',
      description: ''
    };
  }

  /**
   * saveNewField function is used to save a new field added
   */
  saveNewField(): void {
    const key = this.additionalFieldsForm.get('key')?.value;
    const value = this.additionalFieldsForm.get('value')?.value;
    const description = this.additionalFieldsForm.get('description')?.value;

    if (key && description) {
      const latest = this.additionalFieldsForm.get('latest')?.value;
      this.additionalFields.push({ key, value, description, latest });
      this.showAdditionalFields = false;
      this.additionalFieldsForm.reset();
    }
  }

  /**
   * onCancel is invoked to open the dialog box
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * onSave executes the dialog box and emits the data
   */
  onSave(): void {
    const inputValues = {
      ...this.selectedWorkflow.outputs.reduce((acc: any, output: { key: any; value: any; }) => ({ ...acc, [output.key]: output.value }), []),
      ...this.additionalFields.reduce((acc, field) => ({ ...acc, [field.key]: field.value }), [])
    };

    const data: WorkflowData = {
      name: this.selectedWorkflow.wfName,
      target: this.selectedTarget,
      inputValues: inputValues,
    };
    this.dataSaved.emit(data);
    this.dialogRef.close();
  }

  /**Update existing fields */
  updateParametersForm() {
    this.parametersForm = this.fb.group({});

    if (this.selectedWorkflow) {
      this.selectedWorkflow.outputs.forEach((output: any) => {
        this.parametersForm.addControl(output.key, this.fb.control(output.value));
      });
    }
  }
}
